/* THE STAGE, AS DATA. What the world on screen says, from the run analysis: the verdict in a word, why, and the one thing to do about it; the three numbers every run is scored on; and what happened at a bed. Pure. */
import { deepLink, axisHref, type State } from './verdict.ts'
import type { AnalysisJson, Num } from './analysis.ts'
import type { AnalysisBody, Run } from '../types.ts'

export const STAGE_PATHS = (path: string): boolean => path === '/' || path === '/connect' || /^\/bed\/\d+$/.test(path)

export const asJson = (body: AnalysisBody): AnalysisJson => {
  const a = body.analysis as unknown as AnalysisJson
  return { ...a, score: { ...a.score, run_id: body.run.id } }
}

export type Head = { state: State; word: string; why: string; against: string; action: { to: string; label: string } | null }

/** The verdict in a word, why it is that word, and the one action that moves it. */
export function headOf(run: Run, body: AnalysisBody): Head {
  const a = asJson(body)
  const n = a.score.cases
  const v = a.verdict
  const against = v.rules.some((r) => r.from === 'file') ? `Measured against ${v.thresholds.label ?? 'your thresholds file'}` : 'Measured against the default thresholds'
  if (v.result === 'met') return { state: 'met', word: 'Meets your thresholds', why: `Every limit held across ${n} cases.`, against, action: null }
  if (v.result === 'not_met') return { state: 'not_met', word: 'Does not meet your thresholds', why: v.weakest ? `Over the limit on ${v.weakest.name}.` : 'Over your limits.', against, action: null }
  const needed = Math.max(0, ...v.rules.filter((r) => r.status === 'inconclusive').map((r) => r.cases_to_decide ?? 0))
  if (needed <= n) return { state: 'inconclusive', word: `Inconclusive at ${n} cases`, why: 'More cases would not settle it from what was observed.', against, action: null }
  return { state: 'inconclusive', word: `Inconclusive at ${n} cases`, why: `Too few cases to tell. About ${needed} would decide it.`, against, action: { to: `/runs/new?kind=${run.kind}&cases=${needed}`, label: `Run ${Math.min(needed, 400)} cases` } }
}

/** Who was measured. */
const KIND_WORD: Record<string, string> = { sample: 'Sample run', train: 'Training run', evaluation: 'Evaluation run' }
export const whoOf = (run: Run): string => (run.starter ? 'The guideline policy, on a sample run' : run.agent ?? KIND_WORD[run.kind] ?? 'Run')

/** One of the three numbers. `note` says where it stands against its limit, or, for cost, what the dollars are. */
export type Reading = { id: 'outcomes' | 'safety' | 'care'; label: string; value: string; to: string | null; note?: string }
const STANDS: Record<string, string> = { met: 'Within the limit', not_met: 'Over the limit', inconclusive: 'Not settled' }

const firstNum = (o: Record<string, Num>): Num | null => Object.values(o)[0] ?? null

/** The three numbers: outcomes, safety and the care used, in that order. Never summed, and no dollars: those are in the run analysis, beside the outcomes they were spent on. */
export function readingsOf(body: AnalysisBody): Reading[] {
  const a = asJson(body)
  const n = a.score.cases
  const id = a.score.run_id
  const rule = (kind: 'ratio' | 'rate') => a.verdict.rules.find((r) => r.kind === kind && r.status !== 'setting' && r.status !== 'not_measured') ?? null
  const ratio = rule('ratio')
  const rate = rule('rate')
  const severe = firstNum(a.score.numbers.safety)
  const minutes = a.score.burden?.clinician_minutes_per_case
  return [
    { id: 'outcomes', label: 'Outcomes', value: `${a.score.outcome.deaths.died} of ${n} failed`, to: ratio ? axisHref(id, ratio.id) : null, note: ratio ? STANDS[ratio.status] : undefined },
    { id: 'safety', label: 'Safety', value: rate ? `${rate.case_refs.length} of ${n} harmed` : severe && severe.value !== null ? `${Math.round((severe.value * n) / 100)} of ${n} severely harmed` : 'Not recorded', to: rate ? axisHref(id, rate.id) : null, note: rate ? STANDS[rate.status] : undefined },
    { id: 'care', label: 'Care used', value: minutes && minutes.value !== null ? `${Math.round(minutes.value).toLocaleString('en-US')} min of clinician time a case` : 'Not recorded', to: null },
  ]
}

/** World 001 has ten beds; a run's cases take them in turn, so case 12 is played in bed 2. */
export const BEDS = 10
export const bedOf = (bay: number): number => ((Math.max(1, bay) - 1) % BEDS) + 1

export type CaseDot = 'died' | 'harm' | 'short' | 'well'
export const DOT_WORD: Record<CaseDot, string> = { died: 'Failed', harm: 'Harmed', short: 'Not solved', well: 'Solved' }
const dotOf = (c: { died: boolean; harms: number; solved: boolean }): CaseDot => (c.died ? 'died' : c.harms > 0 ? 'harm' : c.solved ? 'well' : 'short')

export type Dotted = { ref: string; bay: number; bed: number; dot: CaseDot; label: string; harms: number }
/** One mark per case, in the order played. */
/** What a case is called: why the patient is here, from the chart, or the world's label once it has one. An evaluation names no case: fresh types are named nowhere. */
export function caseName(c: { label: string; opening?: string }, kind?: string): string {
  if (kind === 'evaluation') return 'Case'
  if (c.opening) return c.opening.replace(/\.$/, '')
  return c.label && !/placeholder/i.test(c.label) ? c.label : 'Case'
}

export const dotsOf = (body: AnalysisBody | null): Dotted[] => (body?.analysis.score.cases_table ?? []).map((c) => ({ ref: c.case_ref, bay: c.bay, bed: bedOf(c.bay), dot: dotOf(c), label: caseName(c, body?.run.kind), harms: c.harms }))

/** The gravest word for a bed: what its label says on the stage. */
export function bedWords(body: AnalysisBody | null): Map<number, CaseDot> {
  const rank: CaseDot[] = ['well', 'short', 'harm', 'died']
  const out = new Map<number, CaseDot>()
  for (const d of dotsOf(body)) {
    const was = out.get(d.bed)
    if (!was || rank.indexOf(d.dot) > rank.indexOf(was)) out.set(d.bed, d.dot)
  }
  return out
}

export type BedLine = { minute: number | null; text: string }
export type BedRead = { dot: CaseDot | null; cases: { ref: string; label: string; dot: CaseDot }[]; lines: BedLine[] }

/** What happened at one bed in this run. */
export function bedRead(body: AnalysisBody | null, bay: number): BedRead {
  if (!body) return { dot: null, cases: [], lines: [] }
  const a = asJson(body)
  const cases = dotsOf(body).filter((d) => d.bed === bay).map((d) => ({ ref: d.ref, label: d.label, dot: d.dot }))
  const harms = (a.score.harms ?? []).filter((h) => bedOf(h.bay) === bay).map((h) => ({ minute: h.minute, text: h.what_happened || h.kind }))
  const windows = (a.score.regret?.costliest ?? []).filter((w) => bedOf(w.bay) === bay).map((w) => ({ minute: w.from_min, text: `To minute ${w.to_min}: ${w.tools.length ? w.tools.join(', ') : 'no action taken'}. Cost ${w.expected_deaths} expected deaths.` }))
  const lines = [...harms, ...windows].sort((x, y) => (x.minute ?? 0) - (y.minute ?? 0))
  return { dot: bedWords(body).get(bay) ?? null, cases, lines }
}

export type CaseRead = { ref: string; number: number; label: string; dot: CaseDot; harms: number; marks: { from: number; to: number; kind: 'harm' | 'window'; text: string; cost: number | null }[]; costed: number }

/** Every case played in one bed, in the order played, with what its record says happened and when. */
export function casesAtBed(body: AnalysisBody | null, bay: number): CaseRead[] {
  if (!body) return []
  const a = asJson(body)
  const all = dotsOf(body)
  const out: CaseRead[] = []
  all.forEach((d, i) => {
    if (d.bed !== bay || out.length >= 40) return
    const marks = [
      ...(a.score.harms ?? []).filter((h) => h.case_ref === d.ref).map((h) => ({ from: h.minute ?? 0, to: h.minute ?? 0, kind: 'harm' as const, text: (h.what_happened || h.kind).replace(/^\w/, (c) => c.toUpperCase()), cost: null })),
      ...(a.score.regret?.costliest ?? []).filter((w) => w.case_ref === d.ref).map((w) => ({ from: w.from_min, to: w.to_min, kind: 'window' as const, text: w.tools.length ? `Acted: ${w.tools.map((t) => t.replace(/_/g, ' ')).join(', ')}` : 'No action taken', cost: w.expected_deaths })),
    ].sort((x, y) => x.from - y.from)
    out.push({ ref: d.ref, number: i + 1, label: d.label, dot: d.dot, harms: Math.max(d.harms, marks.filter((m) => m.kind === 'harm').length), marks, costed: marks.reduce((n, m) => n + (m.cost ?? 0), 0) })
  })
  return out
}

/** A case minute, as a reader says it. */
export const whenText = (m: number): string => (m < 120 ? `Minute ${m}` : `Hour ${Math.floor(m / 60)}`)

export const bedHref = (run: string | null, bay: number): string => (run ? deepLink(run, bay, null) : `/?bed=${bay}`)

/** The bed a location names: `/bed/4`, or `?bed=4`. */
export function bedAtLocation(path: string, query: URLSearchParams): number | null {
  const m = /^\/bed\/(\d+)$/.exec(path)
  const raw = m ? m[1] : query.get('bed')
  const n = Number(raw)
  return raw && Number.isInteger(n) && n >= 1 ? n : null
}

/** How wide the sheet of a page stands over the world, in CSS px. A page that needs the room takes all of it. */
export function sheetWidth(path: string, viewport: number): number {
  const fit = (w: number) => Math.min(w, viewport - 96)
  if (path === '/bedside' || viewport < 760) return viewport
  if (path.startsWith('/bench') || path === '/program' || path.startsWith('/verdict/')) return fit(1120)
  if (path === '/runs' || path === '/compare' || path.startsWith('/agents/')) return fit(1180)
  if (/^\/runs\/[^/]+$/.test(path) && path !== '/runs/new') return fit(1120)
  if (path === '/runs/new') return fit(860)
  if (path === '/license' || path === '/licence') return fit(680)
  return fit(860)
}
